import { useEffect } from 'react'
import debounce from 'lodash/debounce'
import { useDispatch, useSelector } from 'react-redux'
import { ActionModule } from '@typesApp/ActionModule'
import { HostEnvironment, HostSession } from '@typesApp/Environment'
import {
  authTokenError,
  authTokenReset,
  connectorActionModuleSelector,
  connectorEnvironmentSelector,
  connectorInitActionModule,
  connectorInitAnonymousSession,
  connectorInitEnvironment,
  connectorInitSession,
  connectorSessionSelector,
  resetSessionExpired,
  sessionTransitioned,
} from '@redux/slices/connectorSlice'
import {
  initGlobalLocale,
  normalizeEnvironment,
  isEnvironmentChanged,
  isSessionChanged,
  canRequestAuthToken,
  isAnonymousSession,
  normalizeHostSession,
  getSessionWithCustomerContext,
  sessionValid,
  isGuestTransitionToAuthenticated,
  isAuthenticatedTransitionToGuest,
  isNewCustomerAfterSessionExpired,
  getSessionTypeDisplay,
  hasWCTokensWithoutSessionId,
  getCurrentSessionIdOrFetchWithWCTokens,
} from '@utils/appInitUtil'
import { appEventEmitter } from '@utils/appEventEmitter'
import { useSession } from '@hooks/useSession'
import { useSessionInfo } from '@hooks/useSessionInfo'

type UseAppInitParams = {
  session: HostSession
  environment: HostEnvironment
  actionModule: ActionModule
}

export const useAppInit = (params: UseAppInitParams) => {
  const { session: hostSession, environment: hostEnvironment, actionModule } = params
  const dispatch = useDispatch()
  const { initContext, initSession, initContextWithTokens } = useSession()
  const { isTokenError, isTokenReady, isSessionInitializing, isLoggingOut, isSessionExpired } = useSessionInfo()

  const currentEnvironment = useSelector(connectorEnvironmentSelector)
  const currentActionModule = useSelector(connectorActionModuleSelector)
  const currentSession = useSelector(connectorSessionSelector)

  const environment = normalizeEnvironment(hostEnvironment)
  const anonymousUserId = environment?.anonymousUserId

  useEffect(() => {
    if (environment && (!currentEnvironment || isEnvironmentChanged(currentEnvironment, environment))) {
      dispatch(connectorInitEnvironment(environment))
      initGlobalLocale(environment)
    }
  }, [dispatch, environment, currentEnvironment])

  useEffect(() => {
    if (actionModule && !currentActionModule) {
      dispatch(connectorInitActionModule(actionModule))
    }
  }, [dispatch, actionModule, currentActionModule])

  // update session data in redux state
  useEffect(() => {
    if (isTokenError || isSessionInitializing) {
      return
    }

    if (!isSessionChanged(hostSession, currentSession, anonymousUserId)) {
      return
    }

    const debouncedHandler = debounce(async () => {
      const session = normalizeHostSession(hostSession)

      // workaround for CLE bug where session id is not sent but everything else is valid
      // remove once integration has been fixed to send session id
      if (session && hasWCTokensWithoutSessionId(session)) {
        session.sessionId = await getCurrentSessionIdOrFetchWithWCTokens(session, currentSession, initContextWithTokens)
        if (!session.sessionId) {
          console.debug('directbilling session - got empty session id from commerce', session.sessionId)
          dispatch(authTokenError())
          return
        }

        if (!isSessionChanged(session, currentSession, anonymousUserId)) {
          return
        }
        console.debug('directbilling session - using session %s in place of empty session id', session.sessionId)
      }

      const isAnonymous = isAnonymousSession(session, anonymousUserId)
      const isValid = sessionValid({ isLoggingOut, isSessionExpired, session, currentSession })

      if (!session || !isValid) {
        return
      }

      if (isAnonymous) {
        console.debug('directbilling session - anonymous user')
        dispatch(connectorInitAnonymousSession(session))
        return
      }

      // session has expired but now commerce has new customer
      if (isNewCustomerAfterSessionExpired(session, currentSession, isSessionExpired)) {
        console.debug(`directbilling session - new customer number ${session.userId} after session expired`)

        // will trigger hook to re-render and update to latest session data
        dispatch(resetSessionExpired())
        return
      }

      if (!isLoggingOut && !isSessionExpired) {
        if (session?.isLoggedIn || session?.isPartiallyAuthenticated) {
          console.debug(
            'directbilling session - %s user',
            session?.isPartiallyAuthenticated ? 'partially authenticated' : 'authenticated'
          )
        } else if (!session?.isGuest && (session?.sessionId || session?.cartId)) {
          console.debug('directbilling session - unauthenticated user with session or cart')
        }
      }

      // on transition from guest session to authenticated, reset the auth token state
      // to ensure a new one is initialized for authenticated user.
      if (isGuestTransitionToAuthenticated(session, currentSession)) {
        dispatch(authTokenReset())
        console.debug('directbilling session - guest user transition to authenticated user')
      }

      // similar for transition from authenticated to guest (when authenticated session expires).
      if (isAuthenticatedTransitionToGuest(session, currentSession)) {
        dispatch(authTokenReset())
        dispatch(sessionTransitioned())

        // let appEventListener handle state reset
        appEventEmitter.sessionTransitioned()
        console.debug('directbilling session - authenticated user transition to guest user')
      }

      dispatch(connectorInitSession(session))
    })

    debouncedHandler()
    return () => debouncedHandler.cancel()
  }, [
    dispatch,
    initSession,
    initContextWithTokens,
    anonymousUserId,
    currentSession,
    hostSession,
    isLoggingOut,
    isSessionExpired,
    isTokenError,
    isSessionInitializing,
  ])

  // init auth token and active data
  useEffect(() => {
    if (isTokenReady || isSessionInitializing || isTokenError || !currentSession) {
      return
    }

    if (!canRequestAuthToken(currentSession, anonymousUserId)) {
      return
    }

    const debouncedHandler = debounce(async () => {
      console.debug(
        'directbilling session - [%s] sessionId=%s customerNumber=%s cartId=%s',
        getSessionTypeDisplay(currentSession),
        currentSession.sessionId ?? '',
        currentSession?.userId ?? '(pending)',
        currentSession.cartId ?? ''
      )

      // always fetch latest session id from commerce
      const context = await initContext()
      const mergedSession = getSessionWithCustomerContext(currentSession, context)

      await initSession(mergedSession)

      if (isSessionChanged(currentSession, mergedSession, anonymousUserId)) {
        dispatch(connectorInitSession(mergedSession))
      }
    })

    debouncedHandler()
    return () => debouncedHandler.cancel()
  }, [
    dispatch,
    initContext,
    initSession,
    isTokenReady,
    isTokenError,
    isSessionInitializing,
    currentSession,
    anonymousUserId,
  ])
}
